import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Check, Zap } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { subscriptionsAPI, propertiesAPI } from '../services/api';
import toast from 'react-hot-toast';

const PLAN_RANK = { free: 0, basic: 1, pro: 2, agency: 3 };
const PLAN_LIMITS = { free: 1, basic: 3, pro: 10, agency: Infinity };

const BANK_DETAILS = {
  bank: 'Attijariwafa Bank (Bankalik)',
  accountHolder: 'Soufiane Jaafar',
  rib: '007 170 000415730040244189',
  agence: 'El Jadida Mohamed V',
};

function PaymentModal({ plan, planName, onClose, onSubmitted }) {
  const [form, setForm] = useState({ full_name: '', transfer_reference: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await subscriptionsAPI.request({
        plan: plan.id,
        full_name: form.full_name,
        transfer_reference: form.transfer_reference,
      });
      toast.success("Request submitted! We'll activate your plan within 24 hours.");
      onSubmitted();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error submitting request');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <h3 style={{ fontFamily: 'Syne', fontWeight: 700 }}>Subscribe to {planName}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 20 }}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 20 }}>
              <span style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: 28 }}>{plan.price}</span>
              <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>MAD/month</span>
            </div>

            <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: 16, marginBottom: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 10 }}>Bank Transfer Details</div>
              <div style={{ fontSize: 13, lineHeight: 1.8, color: 'var(--text-primary)' }}>
                <div><span style={{ color: 'var(--text-muted)' }}>Bank:</span> {BANK_DETAILS.bank}</div>
                <div><span style={{ color: 'var(--text-muted)' }}>Account holder:</span> {BANK_DETAILS.accountHolder}</div>
                <div><span style={{ color: 'var(--text-muted)' }}>RIB:</span> {BANK_DETAILS.rib}</div>
                <div><span style={{ color: 'var(--text-muted)' }}>Agence:</span> {BANK_DETAILS.agence}</div>
              </div>
            </div>

            <p style={{ fontSize: 13, color: 'var(--accent-light)', background: 'var(--accent-glow)', padding: '10px 14px', borderRadius: 'var(--radius-sm)', marginBottom: 20 }}>
              Your plan will be activated within 24 hours after we confirm your transfer.
            </p>

            <div className="form-group">
              <label>Full Name *</label>
              <input value={form.full_name} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} required />
            </div>
            <div className="form-group">
              <label>Transfer Reference Number *</label>
              <input value={form.transfer_reference} onChange={e => setForm(f => ({ ...f, transfer_reference: e.target.value }))} placeholder="The référence from your bank app" required />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" onClick={onClose} className="btn btn-ghost">Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? <span className="spinner" /> : "I've made the transfer"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const PLANS = [
  {
    id: 'free',
    nameKey: 'pricing.plan.free',
    price: 0,
    accentColor: 'var(--text-secondary)',
    featureKeys: [
      'pricing.feature.1property',
      'pricing.feature.unlimitedBookings',
      'pricing.feature.guestRecords',
      'pricing.feature.incomeReports',
      'pricing.feature.publicLinks',
    ],
    popular: false,
  },
  {
    id: 'basic',
    nameKey: 'pricing.plan.basic',
    price: 149,
    accentColor: 'var(--success)',
    featureKeys: [
      'pricing.feature.upTo3',
      'pricing.feature.unlimitedBookings',
      'pricing.feature.guestRecords',
      'pricing.feature.incomeReports',
      'pricing.feature.publicLinks',
    ],
    popular: false,
  },
  {
    id: 'pro',
    nameKey: 'pricing.plan.pro',
    price: 299,
    accentColor: 'var(--accent-light)',
    featureKeys: [
      'pricing.feature.upTo10',
      'pricing.feature.unlimitedBookings',
      'pricing.feature.guestRecords',
      'pricing.feature.incomeReports',
      'pricing.feature.publicLinks',
      'pricing.feature.prioritySupport',
    ],
    popular: true,
  },
  {
    id: 'agency',
    nameKey: 'pricing.plan.agency',
    price: 499,
    accentColor: 'var(--gold)',
    featureKeys: [
      'pricing.feature.unlimitedProperties',
      'pricing.feature.unlimitedBookings',
      'pricing.feature.guestRecords',
      'pricing.feature.incomeReports',
      'pricing.feature.publicLinks',
      'pricing.feature.prioritySupport',
    ],
    popular: false,
  },
];

export default function Pricing() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [paymentPlan, setPaymentPlan] = useState(null);
  const [propertyCount, setPropertyCount] = useState(0);

  useEffect(() => {
    if (user) {
      propertiesAPI.getAll().then(res => {
        const props = res.data.data || res.data || [];
        setPropertyCount(Array.isArray(props) ? props.length : 0);
      }).catch(() => {});
    }
  }, [user]);

  const currentPlan = user?.subscription_plan || 'free';

  const handleSelectPlan = (plan) => {
    if (!user) { navigate('/register'); return; }

    // Already on this plan
    if (plan.id === currentPlan) return;

    // Free plan — downgrade to free
    if (plan.id === 'free') {
      const freeLimit = PLAN_LIMITS.free;
      if (propertyCount > freeLimit) {
        toast.error(`You have ${propertyCount} properties. Delete down to ${freeLimit} before switching to Free.`);
        return;
      }
      toast.success('To switch to Free, please contact support.');
      return;
    }

    // Downgrade — check property count fits target plan
    if (PLAN_RANK[plan.id] < PLAN_RANK[currentPlan]) {
      const targetLimit = PLAN_LIMITS[plan.id];
      if (propertyCount > targetLimit) {
        toast.error(`You have ${propertyCount} properties. Delete down to ${targetLimit} before downgrading to ${t(plan.nameKey)}.`);
        return;
      }
    }

    setPaymentPlan(plan);
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', padding: '60px 24px 80px' }}>
      <div style={{ maxWidth: 1040, margin: '0 auto' }}>

        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 52 }}>
          <div style={{ width: 38, height: 38, borderRadius: 10, background: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 0 24px rgba(108,99,255,0.4)' }}>
            <Zap size={18} color="white" fill="white" />
          </div>
          <span style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: 20, letterSpacing: '-0.5px' }}>Nuzlio</span>
        </div>

        {/* Heading */}
        <div style={{ textAlign: 'center', marginBottom: 52 }}>
          <h1 style={{ fontFamily: 'Syne', fontSize: 42, fontWeight: 800, letterSpacing: '-1.5px', marginBottom: 14 }}>
            {t('pricing.title')}
          </h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: 16, maxWidth: 460, margin: '0 auto', lineHeight: 1.6 }}>
            {t('pricing.subtitle')}
          </p>
        </div>

        {/* Plan cards */}
        <div className="grid-4" style={{ alignItems: 'start' }}>
          {PLANS.map(plan => {
            const isCurrent = plan.id === currentPlan;
            const isDowngrade = !!user && PLAN_RANK[plan.id] < PLAN_RANK[currentPlan];
            const planName = t(plan.nameKey);
            return (
              <div
                key={plan.id}
                style={{
                  position: 'relative',
                  background: plan.popular
                    ? 'linear-gradient(160deg, rgba(108,99,255,0.12) 0%, var(--bg-card) 60%)'
                    : 'var(--bg-card)',
                  border: `1px solid ${isCurrent ? 'var(--success)' : plan.popular ? 'var(--accent)' : 'var(--border)'}`,
                  borderRadius: 'var(--radius)',
                  padding: '28px 22px',
                  boxShadow: plan.popular ? '0 0 48px rgba(108,99,255,0.18)' : 'none',
                  transition: 'border-color 0.2s',
                }}
              >
                {plan.popular && (
                  <div style={{
                    position: 'absolute', top: -13, left: '50%', transform: 'translateX(-50%)',
                    background: 'var(--accent)', color: 'white', fontSize: 10, fontWeight: 700,
                    padding: '4px 14px', borderRadius: 20, letterSpacing: '0.06em',
                    textTransform: 'uppercase', whiteSpace: 'nowrap',
                  }}>
                    {t('pricing.mostPopular')}
                  </div>
                )}

                <div style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: 18, marginBottom: 16, color: plan.accentColor }}>
                  {planName}
                </div>

                <div style={{ marginBottom: 24 }}>
                  {plan.price === 0 ? (
                    <span style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: 38, color: 'var(--text-primary)' }}>
                      {t('pricing.plan.free')}
                    </span>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                      <span style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: 38, color: 'var(--text-primary)' }}>{plan.price}</span>
                      <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>MAD/mo</span>
                    </div>
                  )}
                </div>

                <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 11, marginBottom: 28 }}>
                  {plan.featureKeys.map(key => (
                    <li key={key} style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 13, color: 'var(--text-secondary)' }}>
                      <Check size={14} color={plan.accentColor} strokeWidth={2.5} style={{ flexShrink: 0 }} />
                      {t(key)}
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handleSelectPlan(plan)}
                  disabled={isCurrent}
                  className={plan.popular && !isCurrent ? 'btn btn-primary' : 'btn btn-ghost'}
                  style={{ width: '100%', justifyContent: 'center', padding: '11px', opacity: isCurrent ? 0.6 : 1, cursor: isCurrent ? 'default' : 'pointer' }}
                >
                  {isCurrent
                    ? t('pricing.btn.currentPlan')
                    : isDowngrade
                    ? `Downgrade to ${planName}`
                    : plan.price === 0
                    ? t('pricing.btn.getStartedFree')
                    : t('pricing.btn.start', planName)}
                </button>
              </div>
            );
          })}
        </div>

        <p style={{ textAlign: 'center', marginTop: 48, fontSize: 14, color: 'var(--text-muted)' }}>
          {user ? (
            <Link to="/dashboard" style={{ color: 'var(--accent-light)', textDecoration: 'none' }}>{t('pricing.btn.backToDashboard')}</Link>
          ) : (
            <>Already have an account?{' '}<Link to="/login" style={{ color: 'var(--accent-light)', textDecoration: 'none', fontWeight: 500 }}>Sign in</Link></>
          )}
        </p>
      </div>

      {paymentPlan && (
        <PaymentModal
          plan={paymentPlan}
          planName={t(paymentPlan.nameKey)}
          onClose={() => setPaymentPlan(null)}
          onSubmitted={() => setPaymentPlan(null)}
        />
      )}
    </div>
  );
}
